import React, { useEffect, useMemo, useState } from 'react';
import { consumeNavPath } from '@/lib/adminNav';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from '@/components/ui/use-toast';
import { supabase } from '@/lib/supabase';
import SettingsSidebarLayout from '@/components/ui/settings-sidebar-layout';
import type { SettingsSideNavItem } from '@/components/ui/settings-side-nav';
import IvrApiKeys from './IvrSettings';
import IvrPoiSettings from './IvrPoiSettings';
import IvrBotTrainingTab from './IvrBotTrainingTab';
import {
  Building2,
  MessageSquare,
  MapPin,
  MapPinned,
  Search,
  Clock,
  Car,
  Sparkles,
  Phone,
  Settings2,
  KeyRound,
  Mail,
  Eye,
  EyeOff,
  Loader2,
  Save,
  GraduationCap,
  type LucideIcon,
} from 'lucide-react';

interface IvrSettingRow {
  name: string;
  value: string | null;
  label: string | null;
  description: string | null;
  type: string;
  sort_order: number;
}

// Rows whose name suggests a credential (API key/secret/SID/token) are masked
// by default -- these values live in the generic `settings` table in plain
// text either way (same known constraint as everywhere else in this app),
// this is just screen hygiene so they aren't visible by default in the office.
const isSecretField = (name: string) => /key|secret|sid|token/i.test(name);

// A handful of fields (long comma-separated hint lists, greeting scripts with
// a description) read better as a multi-line box than a single-line input.
const isLongField = (row: IvrSettingRow) =>
  row.type !== 'number' && ((row.value?.length ?? 0) > 80 || !!row.description);

// The `settings` table has no per-row category finer than "IVR" itself, so
// this groups by name prefix, curated by hand for a sensible reading order
// (behavior settings first, technical credentials last since they're touched
// least often). Anything that doesn't match falls into "Other" rather than
// being dropped, so a new ivr.* setting the developer adds later still shows
// up somewhere instead of silently disappearing.
const CATEGORIES: {
  key: string;
  title: string;
  description: string;
  icon: LucideIcon;
  match: (name: string) => boolean;
}[] = [
  { key: 'general', title: 'General', description: 'Company identity', icon: Building2, match: (n) => n === 'ivr.company_name' },
  { key: 'greetings', title: 'Greetings', description: 'Caller greeting scripts', icon: MessageSquare, match: (n) => n.startsWith('ivr.greeting_') },
  { key: 'service-area', title: 'Service Area', description: 'Geofence & radius', icon: MapPin, match: (n) => n.startsWith('ivr.service_') },
  { key: 'address', title: 'Address Recognition', description: 'Speech hints & trimming', icon: Search, match: (n) => n.startsWith('ivr.address_') },
  {
    key: 'booking-rules',
    title: 'Booking Rules',
    description: 'Timing & advance limits',
    icon: Clock,
    match: (n) =>
      [
        'ivr.booking_ttl_seconds',
        'ivr.max_advance_days',
        'ivr.min_pickup_minutes',
        'ivr.min_spoken_eta_seconds',
      ].includes(n),
  },
  { key: 'taxicaller', title: 'TaxiCaller', description: 'Dispatch API credentials', icon: Car, match: (n) => n.startsWith('ivr.taxicaller_') },
  { key: 'openai', title: 'OpenAI', description: 'Realtime voice model', icon: Sparkles, match: (n) => n.startsWith('ivr.openai_') },
  { key: 'synopsis', title: 'Weekly Synopsis', description: 'Where the Sunday report gets emailed', icon: Mail, match: (n) => n.startsWith('ivr.synopsis_') },
  { key: 'twilio', title: 'Twilio & Call Routing', description: 'Telephony & bailout', icon: Phone, match: (n) => n.startsWith('ivr.twilio_') || n === 'ivr.bailout_sip_uri' },
];

interface CategoryGroup {
  key: string;
  title: string;
  description: string;
  icon: LucideIcon;
  rows: IvrSettingRow[];
}

function groupByCategory(rows: IvrSettingRow[]): CategoryGroup[] {
  const remaining = [...rows];
  const groups: CategoryGroup[] = [];
  for (const cat of CATEGORIES) {
    const matched: IvrSettingRow[] = [];
    for (let i = remaining.length - 1; i >= 0; i--) {
      if (cat.match(remaining[i].name)) matched.unshift(...remaining.splice(i, 1));
    }
    if (matched.length > 0) groups.push({ ...cat, rows: matched });
  }
  if (remaining.length > 0) {
    groups.push({ key: 'other', title: 'Other', description: 'Everything else', icon: Settings2, rows: remaining });
  }
  return groups;
}

const API_KEYS_TAB = 'api-keys';
const POI_TAB = 'points-of-interest';
const TRAINING_TAB = 'bot-training';

const IvrGeneralSettings: React.FC = () => {
  const [rows, setRows] = useState<IvrSettingRow[]>([]);
  const [values, setValues] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [revealed, setRevealed] = useState<Record<string, boolean>>({});
  const [activeCategory, setActiveCategory] = useState<string>('general');

  // Deep-link from the Site Map (Settings -> Site Map) -- see adminNav.ts.
  // path[0] ("ivr") is consumed by AdminDashboard.tsx for settingsSubTab;
  // this reads path[1] for the category.
  useEffect(() => {
    const path = consumeNavPath();
    if (path?.[1]) setActiveCategory(path[1]);
  }, []);

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('settings')
      .select('name, value, label, description, type, sort_order')
      .eq('category', 'IVR')
      .order('sort_order', { ascending: true });
    if (error) {
      console.error('Error loading IVR settings:', error);
      toast({ title: 'Error', description: 'Failed to load IVR settings', variant: 'destructive' });
    } else {
      const loaded = (data as IvrSettingRow[]) || [];
      setRows(loaded);
      setValues(Object.fromEntries(loaded.map((r) => [r.name, r.value ?? ''])));
    }
    setLoading(false);
  };

  const groups = useMemo(() => groupByCategory(rows), [rows]);
  const currentGroup = groups.find((g) => g.key === activeCategory);
  const showingApiKeys = activeCategory === API_KEYS_TAB;
  const showingPois = activeCategory === POI_TAB;
  const showingTraining = activeCategory === TRAINING_TAB;
  const showingCustomTab = showingApiKeys || showingPois || showingTraining;

  const navTabs: SettingsSideNavItem[] = useMemo(
    () => [
      ...groups.map((g) => ({ value: g.key, label: g.title, description: g.description, icon: g.icon })),
      { value: POI_TAB, label: 'Points of Interest', description: 'Local landmarks for address matching', icon: MapPinned },
      { value: TRAINING_TAB, label: 'Bot Training', description: 'Q&A content for the IVR to draw on', icon: GraduationCap },
      { value: API_KEYS_TAB, label: 'API Keys', description: 'Keys for the IVR developer', icon: KeyRound },
    ],
    [groups]
  );

  const isDirty = rows.some((r) => (values[r.name] ?? '') !== (r.value ?? ''));

  const handleSave = async () => {
    setSaving(true);
    try {
      const changed = rows.filter((r) => (values[r.name] ?? '') !== (r.value ?? ''));
      const results = await Promise.all(
        changed.map((r) =>
          supabase.from('settings').update({ value: values[r.name] }).eq('name', r.name)
        )
      );
      const failed = results.find((r) => r.error);
      if (failed?.error) throw failed.error;
      toast({ title: 'Saved', description: 'IVR settings updated.' });
      await load();
    } catch (err) {
      console.error('Error saving IVR settings:', err);
      toast({ title: 'Error', description: (err as Error)?.message || 'Failed to save IVR settings', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-10">
        <Loader2 className="w-6 h-6 animate-spin text-yellow-500" />
      </div>
    );
  }

  return (
    <Card className="shadow-lg bg-card border border-border overflow-hidden">
      <CardHeader className="border-b border-border bg-gradient-to-r from-zinc-950 via-zinc-900 to-zinc-950">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <CardTitle className="text-xl text-primary flex items-center gap-2">
              <Phone className="w-5 h-5" />
              IVR Settings
            </CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              Configuration, credentials and API keys the IVR developer needs for the phone booking assistant.
            </p>
          </div>
          {!showingCustomTab && (
            <Button size="sm" onClick={handleSave} disabled={saving || !isDirty} className="self-start md:self-auto">
              {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />}
              Save Changes
            </Button>
          )}
        </div>
      </CardHeader>

      <CardContent className="p-0 bg-zinc-950">
        <SettingsSidebarLayout tabs={navTabs} value={activeCategory} onValueChange={setActiveCategory} minHeight="min-h-[460px]">
          {showingApiKeys ? (
            <IvrApiKeys />
          ) : showingPois ? (
            <IvrPoiSettings />
          ) : showingTraining ? (
            <IvrBotTrainingTab />
          ) : currentGroup ? (
            <div className="space-y-5">
              <div>
                <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
                  <currentGroup.icon className="h-5 w-5 text-yellow-500" />
                  {currentGroup.title}
                </h3>
                <p className="text-sm text-muted-foreground mt-1">{currentGroup.description}</p>
              </div>

              <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                {currentGroup.rows.map((row) => {
                  const secret = isSecretField(row.name);
                  const long = isLongField(row);
                  const value = values[row.name] ?? '';
                  const shown = revealed[row.name];

                  return (
                    <div key={row.name} className={`space-y-1.5 ${long ? 'xl:col-span-2' : ''}`}>
                      <Label className="text-foreground text-sm">{row.label || row.name}</Label>
                      {row.description && <p className="text-xs text-gray-500">{row.description}</p>}
                      <div className="relative">
                        {long ? (
                          <textarea
                            value={value}
                            onChange={(e) => setValues((prev) => ({ ...prev, [row.name]: e.target.value }))}
                            className="dark-input w-full min-h-[70px] rounded-md px-3 py-2 text-sm resize-y"
                          />
                        ) : (
                          <Input
                            type={secret && !shown ? 'password' : row.type === 'number' ? 'number' : 'text'}
                            value={value}
                            onChange={(e) => setValues((prev) => ({ ...prev, [row.name]: e.target.value }))}
                            className={`dark-input ${secret ? 'pr-9' : ''}`}
                          />
                        )}
                        {secret && !long && (
                          <button
                            type="button"
                            onClick={() => setRevealed((prev) => ({ ...prev, [row.name]: !prev[row.name] }))}
                            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
                            title={shown ? 'Hide value' : 'Show value'}
                          >
                            {shown ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <p className="text-sm text-gray-500">No IVR settings found yet.</p>
          )}
        </SettingsSidebarLayout>
      </CardContent>
    </Card>
  );
};

export default IvrGeneralSettings;
